/**
 * How a date reads — in the SIGNED document, on the signing screen, and in the console.
 *
 * `validateFieldValue` stores every date as `YYYY-MM-DD`, and `packages/core/src/fields/value.ts`
 * says why: "`date_format` is a presentation choice and belongs to the renderer", because
 * `03/04/2026` means two different days on two continents. This is that renderer-side choice, made
 * once per organization (its default team's `workspace_settings.document_date_format`, ADR 0067;
 * a team with its own row overrides it), frozen into the send-time snapshot, and read by three
 * renderers (ADR 0080, ADR 0132):
 *
 *   * the completion consumer, for every date value it stamps into the PDF;
 *   * the signing ceremony, for the date signed it shows a signer and the "on the document" line
 *     beside a date they type — from the same frozen snapshot, so screen and PDF agree;
 *   * the console, for the dates on the document lists — from the live row, because a list is not
 *     evidence and should read the way the organization reads today.
 *
 * Five formats, named by their shape rather than by a country, and the stored value is untouched:
 * an organization that changes its mind tomorrow changes how tomorrow's documents read, never what
 * last week's signer answered.
 */

export const DOCUMENT_DATE_FORMATS = [
  'yyyy-mm-dd',
  'dd/mm/yyyy',
  'mm/dd/yyyy',
  'dd mmm yyyy',
  'mmmm d, yyyy',
] as const

export type DocumentDateFormat = (typeof DOCUMENT_DATE_FORMATS)[number]

export const isDocumentDateFormat = (value: unknown): value is DocumentDateFormat =>
  typeof value === 'string' && (DOCUMENT_DATE_FORMATS as readonly string[]).includes(value)

/**
 * Day-first, the founder's default for the launch market (2026-09-16: "as per international
 * standard show them DD/MM/YYYY"). `20260915005090` moved the column default with it and rewrote
 * the rows that had never chosen — measured, every row on the cell was at the old default.
 *
 * This is the default for an organization that has NOT chosen: a new team's row, a console before
 * the organization has loaded, a policy row with no value. It is NOT the fallback for a snapshot
 * that predates the column — that is the next constant.
 */
export const DEFAULT_DOCUMENT_DATE_FORMAT: DocumentDateFormat = 'dd/mm/yyyy'

/**
 * What an envelope sent BEFORE `20260913000600` renders in.
 *
 * Its `delivery_policy_snapshot` carries no `document_date_format` at all, and it was sent when the
 * only rendering was the stored ISO form — so that is what its document reads in, and what its
 * signing screen must show, whatever the organization has chosen since. ADR 0080: documents already
 * out keep the rules they were sent under. Both readers of a snapshot fall back to THIS, never to
 * the default above; a snapshot with the key uses the key.
 */
export const LEGACY_DOCUMENT_DATE_FORMAT: DocumentDateFormat = 'yyyy-mm-dd'

/**
 * The shape a frozen snapshot asks for, or the legacy one when the snapshot predates the column.
 *
 * One function for the two readers of a snapshot (the flatten and the ceremony's options), so
 * they cannot disagree about an old envelope: a value that is not one of the five is treated as
 * absent rather than coerced, because the CHECK never admitted such a value and its presence means
 * a hand-edited row, not a choice.
 *
 * **Pass it the RAW snapshot key, never `policyText(policy, 'document_date_format')`.** `policyText`
 * substitutes `POLICY_DEFAULTS` — the COLUMN's default — for an absent key, which is the right
 * answer for every other setting and the wrong one here: an envelope sent before the column existed
 * would then render in whatever the default says TODAY. Written that way first, and
 * `completion-consumer.test.ts`'s "flattens with today's rendering when the policy cannot be read"
 * caught it: the flatten drew `04/09/2026` while the ceremony, which reads the raw key, drew
 * `2026-09-04` — the screen and the paper disagreeing about a date, which is the exact defect ADR
 * 0126 exists to close, reintroduced by its own fix.
 */
export function snapshotDateFormat(value: unknown): DocumentDateFormat {
  return isDocumentDateFormat(value) ? value : LEGACY_DOCUMENT_DATE_FORMAT
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `2026-09-04` in the team's format, or the input unchanged when it is not a canonical date or the
 * format is not one of the five. Unchanged rather than thrown: this runs inside the flatten, and a
 * value the validator let through is the signer's answer whatever shape it is in — the flatten's
 * job is to draw it, not to refuse it.
 */
export function formatDocumentDate(value: string, format: string): string {
  const match = ISO_DATE.exec(value)
  if (match === null || !isDocumentDateFormat(format)) return value
  const [, yyyy = '', mm = '', dd = ''] = match
  const monthIndex = Number(mm) - 1
  const month = MONTHS[monthIndex]
  if (month === undefined) return value
  switch (format) {
    case 'yyyy-mm-dd':
      return value
    case 'dd/mm/yyyy':
      return `${dd}/${mm}/${yyyy}`
    case 'mm/dd/yyyy':
      return `${mm}/${dd}/${yyyy}`
    case 'dd mmm yyyy':
      return `${dd} ${month.slice(0, 3)} ${yyyy}`
    case 'mmmm d, yyyy':
      return `${month} ${Number(dd)}, ${yyyy}`
  }
}

/** One example per format, for a settings screen to show beside the choice. */
export function describeDocumentDateFormat(format: DocumentDateFormat): string {
  return formatDocumentDate('2026-09-04', format)
}
